import { FacilityListParams } from '@gyym/sdk'
import { Button, Empty, Grid, Link, Typography } from '@gyym/ui'
import { Area, Conditions, Facility, LocationExact } from '@gyym/ui/icons'
import { useRouter } from 'next/router'
import React from 'react'

import { useActivities } from '@/modules/Activities'
import {
  searchHistory,
  searchQueryConverter,
  useSearchAreaModal,
  useSearchConditionsModal,
  useSearchFacilityNameModal,
  useSearchHistories,
} from '@/modules/FacilitySearch'
import { usePrefectures } from '@/modules/Prefectures'

import { Category, CategoryButtonInner, History } from './styles'

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SearchViewProps {}

export const SearchView = (_: SearchViewProps) => {
  const histories = useSearchHistories()
  const { data: prefectures } = usePrefectures()
  const { data: activities } = useActivities()
  const router = useRouter()

  const openAreaModal = useSearchAreaModal()
  const openFacilityNameModal = useSearchFacilityNameModal()
  const openConditionsModal = useSearchConditionsModal()

  const search = React.useCallback(
    (values: FacilityListParams) => {
      const { href, as } = searchQueryConverter.toLink(
        values,
        prefectures ?? [],
      )

      // Navigate to search result
      router.push(href, as)
    },
    [prefectures, router],
  )

  const searchWithHistory = React.useCallback(
    (values: FacilityListParams) => {
      // Set search history
      searchHistory.set(values)

      router.replace('/search', undefined, { shallow: true })

      search(values)
    },
    [search, router],
  )

  React.useEffect(() => {
    if (!router.query.type) return

    switch (router.query.type) {
      case 'area':
        return openAreaModal({
          onSubmit: searchWithHistory,
          onClose: () => {
            router.replace('/search', undefined, { shallow: true })
          },
        })
      case 'name':
        return openFacilityNameModal({
          onHistoryNavigate: search,
          onSubmit: searchWithHistory,
          onClose: () => {
            router.replace('/search', undefined, { shallow: true })
          },
        })
      case 'condition':
        return openConditionsModal({
          onSubmit: searchWithHistory,
          onClose: () => {
            router.replace('/search', undefined, { shallow: true })
          },
        })
      default:
        return
    }
  }, [
    openAreaModal,
    openConditionsModal,
    openFacilityNameModal,
    router,
    search,
    searchWithHistory,
  ])

  return (
    <>
      <Typography variant="headline1" as="h1">
        検索MENU
      </Typography>

      <Typography variant="subhead1" as="h2" mt={6}>
        検索カテゴリ
      </Typography>

      <Grid as="ul" mt={4} columns={[1, 1, 3]} gap={[0, 0, 4]}>
        <Category>
          <Link href="?type=area" replace shallow>
            <CategoryButtonInner>
              <Area />
              エリアから探す
            </CategoryButtonInner>
          </Link>
        </Category>
        <Category>
          <Link href="/facilities?view=map">
            <CategoryButtonInner>
              <LocationExact />
              現在地から探す
            </CategoryButtonInner>
          </Link>
        </Category>
        <Category>
          <Link href="?type=name" replace shallow>
            <CategoryButtonInner>
              <Facility />
              施設名から探す
            </CategoryButtonInner>
          </Link>
        </Category>
        <Category>
          <Link href="?type=condition" replace shallow>
            <CategoryButtonInner>
              <Conditions />
              条件を指定して探す
            </CategoryButtonInner>
          </Link>
        </Category>
      </Grid>

      <Typography variant="subhead1" as="h2" mt={6}>
        検索履歴
      </Typography>

      {histories.length === 0 && <Empty mt={4}>検索履歴がありません</Empty>}

      {histories.length > 0 && (
        <Grid as="ul" mt={4} columns={1}>
          {histories.map((history, i) => {
            const { href, as } = searchQueryConverter.toLink(
              history,
              prefectures ?? [],
            )
            return (
              <History key={i}>
                <Button asChild>
                  <Link href={href} as={as}>
                    {searchHistory.toReadableString(
                      history,
                      prefectures ?? [],
                      activities ?? [],
                    )}
                  </Link>
                </Button>
              </History>
            )
          })}
        </Grid>
      )}
    </>
  )
}
